/**
 * Block ordering, and the conversion between a document and markdown.
 *
 * Shared by the server, the web editor and the mobile editor so that a
 * paragraph inserted on a phone lands in the same place for everyone, and a
 * document exported as markdown reads the same wherever it was written.
 */
/* ------------------------------------------------------------------ */
/* Ordering                                                            */
/* ------------------------------------------------------------------ */
/**
 * The gap left between paragraphs, so there is always room to insert.
 *
 * Named apart from the board's `ORDER_STEP`: same number, unrelated concept,
 * and one file importing both should not have to wonder which it got.
 */
export const DOC_ORDER_STEP = 1000;
/**
 * Picks an `order` that sits between two paragraphs.
 *
 * Fractions rather than indexes: inserting in the middle of a document must
 * not renumber every paragraph below it, because those renumbered paragraphs
 * would be broadcast as edits and collide with whatever someone else is
 * typing down there. Only the new paragraph changes.
 */
export function orderBetween(before, after) {
    if (before === undefined && after === undefined)
        return DOC_ORDER_STEP;
    if (before === undefined)
        return after - DOC_ORDER_STEP;
    if (after === undefined)
        return before + DOC_ORDER_STEP;
    return (before + after) / 2;
}
/**
 * Whether two paragraphs have drifted too close to fit another between them.
 *
 * Repeatedly inserting in the same spot halves the gap each time, and after
 * about fifty rounds the two orders are the same float. Callers check this
 * and renumber that one document rather than silently losing the position.
 */
export const needsRenumber = (before, after) => Math.abs(after - before) < 1e-6;
export const sortBlocks = (blocks) => [...blocks].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
/** Spaces every paragraph out again, for the rare document that ran out of room. */
export function renumber(blocks) {
    return sortBlocks(blocks).map((block, index) => ({
        ...block,
        order: (index + 1) * DOC_ORDER_STEP,
    }));
}
/* ------------------------------------------------------------------ */
/* Markdown                                                            */
/* ------------------------------------------------------------------ */
const PREFIX = {
    p: '',
    h1: '# ',
    h2: '## ',
    h3: '### ',
    quote: '> ',
    code: '',
    bullet: '- ',
    number: '1. ',
    todo: '- [ ] ',
    divider: '',
};
/** One paragraph as the markdown line a person would have typed. */
export function blockToMarkdown(block) {
    if (block.style === 'divider')
        return '---';
    if (block.style === 'code')
        return `\`\`\`\n${block.text}\n\`\`\``;
    if (block.style === 'todo')
        return `- [${block.checked ? 'x' : ' '}] ${block.text}`;
    return `${PREFIX[block.style]}${block.text}`;
}
export function blocksToMarkdown(blocks) {
    return sortBlocks(blocks).map(blockToMarkdown).join('\n\n');
}
/** The plain prose, for word counts and search — no markers, just words. */
export function blocksToText(blocks) {
    return sortBlocks(blocks)
        .filter((block) => block.style !== 'divider')
        .map((block) => block.text)
        .join('\n');
}
export function wordCount(blocks) {
    const text = blocksToText(blocks).trim();
    return text ? text.split(/\s+/).length : 0;
}
/** Reads one markdown line back into a style and its text. */
export function parseMarkdownLine(line) {
    const trimmed = line.trimEnd();
    if (/^\s*(---|\*\*\*|___)\s*$/.test(trimmed))
        return { style: 'divider', text: '' };
    const todo = /^\s*[-*]\s+\[([ xX])\]\s+(.*)$/.exec(trimmed);
    if (todo)
        return { style: 'todo', text: todo[2], checked: todo[1].toLowerCase() === 'x' };
    const heading = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (heading) {
        const level = heading[1].length;
        return { style: level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3', text: heading[2] };
    }
    const quote = /^>\s?(.*)$/.exec(trimmed);
    if (quote)
        return { style: 'quote', text: quote[1] };
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(trimmed);
    if (bullet)
        return { style: 'bullet', text: bullet[1] };
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(trimmed);
    if (numbered)
        return { style: 'number', text: numbered[1] };
    return { style: 'p', text: trimmed };
}
/**
 * Turns markdown — or plain text, which is markdown with nothing in it — into
 * paragraphs.
 *
 * Blank lines separate paragraphs, except inside a fenced code block, where
 * they are part of the code.
 */
export function markdownToBlocks(source, makeId, userId, now) {
    const blocks = [];
    const lines = source.replace(/\r\n?/g, '\n').split('\n');
    const push = (style, text, checked) => {
        blocks.push({
            id: makeId(),
            text,
            style,
            ...(checked === undefined ? {} : { checked }),
            order: (blocks.length + 1) * DOC_ORDER_STEP,
            updatedBy: userId,
            updatedAt: now,
        });
    };
    let paragraph = [];
    const flushParagraph = () => {
        if (!paragraph.length)
            return;
        push('p', paragraph.join(' ').trim());
        paragraph = [];
    };
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (/^\s*```/.test(line)) {
            flushParagraph();
            const code = [];
            i += 1;
            while (i < lines.length && !/^\s*```/.test(lines[i])) {
                code.push(lines[i]);
                i += 1;
            }
            push('code', code.join('\n'));
            continue;
        }
        if (!line.trim()) {
            flushParagraph();
            continue;
        }
        const parsed = parseMarkdownLine(line);
        if (parsed.style === 'p') {
            // Consecutive plain lines are one paragraph, the way markdown reads
            // them — a hard-wrapped sentence is not five paragraphs.
            paragraph.push(parsed.text);
            continue;
        }
        flushParagraph();
        push(parsed.style, parsed.text, parsed.checked);
    }
    flushParagraph();
    if (!blocks.length)
        push('p', '');
    return blocks;
}
const MARKERS = {
    bold: '**',
    italic: '_',
    strike: '~~',
    code: '`',
};
/**
 * Puts a mark around `[start, end)`, or takes it off again.
 *
 * Toggling rather than adding: pressing bold on something already bold is
 * how every editor of this shape behaves, and without it the only way back
 * is to delete the asterisks by hand. An empty selection inserts the pair
 * and puts the caret between them, so the button works before the words
 * exist as well as after.
 */
export function toggleMark(text, start, end, mark) {
    const marker = MARKERS[mark];
    const before = text.slice(0, start);
    const selected = text.slice(start, end);
    const after = text.slice(end);
    /* Already marked, one way or the other: the markers can sit inside the
       selection (the person selected the whole `**word**`) or just outside it
       (they selected the word between them). Both have to come off, or the
       button would only untoggle for one way of selecting. */
    if (selected.startsWith(marker) &&
        selected.endsWith(marker) &&
        selected.length > marker.length * 2) {
        const bare = selected.slice(marker.length, selected.length - marker.length);
        return {
            text: `${before}${bare}${after}`,
            selectionStart: start,
            selectionEnd: start + bare.length,
        };
    }
    if (before.endsWith(marker) && after.startsWith(marker)) {
        const trimmed = before.slice(0, before.length - marker.length);
        return {
            text: `${trimmed}${selected}${after.slice(marker.length)}`,
            selectionStart: trimmed.length,
            selectionEnd: trimmed.length + selected.length,
        };
    }
    const marked = `${marker}${selected}${marker}`;
    return {
        text: `${before}${marked}${after}`,
        selectionStart: start + marker.length,
        selectionEnd: start + marker.length + selected.length,
    };
}
/**
 * Turns a selection into a markdown link.
 *
 * The selected words become the link text, and an empty selection uses the
 * address as its own text — which is what somebody who pasted a URL and
 * pressed the button meant.
 */
export function insertLink(text, start, end, href) {
    const label = text.slice(start, end) || href;
    const link = `[${label}](${href})`;
    return {
        text: `${text.slice(0, start)}${link}${text.slice(end)}`,
        selectionStart: start + 1,
        selectionEnd: start + 1 + label.length,
    };
}
